import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Heart } from "lucide-react";

/**
 * Contact — Public contact page with business email only.
 * NEVER displays personal contact info.
 */
export default function Contact() {
  const BUSINESS_EMAIL = "temple@mindbodyspirithygienetools.com";

  return (
    <div className="min-h-screen bg-[#efeae7]" data-testid="contact-page">
      {/* Header */}
      <header className="bg-[#042F2E] py-6">
        <div className="max-w-3xl mx-auto px-6 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#5EEAD4] hover:text-white transition-colors font-sans text-sm"
            data-testid="contact-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Home
          </Link>
          <Link to="/build-your-own" className="text-[#5EEAD4] hover:text-white transition-colors font-sans text-sm" data-testid="contact-nav-build-your-own">
            Build Your Own
          </Link>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-3xl mx-auto px-6 py-16 md:py-24">
        {/* Kicker */}
        <p
          className="text-[11px] tracking-[0.3em] font-sans font-semibold uppercase text-[#25a1c2] mb-4"
          data-testid="contact-kicker"
        >
          Mind Body Spirit HYGIENE™
        </p>

        {/* Headline */}
        <h1
          className="font-serif text-4xl md:text-6xl tracking-tighter text-[#042F2E] leading-[1.05] mb-8"
          data-testid="contact-headline"
        >
          Let's connect.<br />
          <span className="italic text-[#25a1c2]">I love hearing from you.</span>
        </h1>

        {/* Gold divider */}
        <div className="flex items-center gap-3 mb-10">
          <div className="h-[1px] w-12" style={{ backgroundColor: "#d4af6a" }} />
          <span style={{ color: "#d4af6a" }} className="text-xs">✦</span>
          <div className="h-[1px] flex-1" style={{ backgroundColor: "#d4af6a", opacity: 0.4 }} />
        </div>

        {/* Body copy */}
        <div className="space-y-5 font-sans text-lg text-[#068fa2] leading-relaxed mb-12">
          <p className="font-serif text-2xl italic text-[#042F2E]">
            Whether it's a question, a thank you, a press inquiry, or a quiet moment of "me too" — I want to hear it.
          </p>
          <p>
            I read every single email myself. I cannot always respond same-day — but every message is received, and most get a personal reply within a few days.
          </p>
          <p>
            For media, podcast, speaking, or partnership inquiries — please include the words <em>"Press"</em> or <em>"Podcast"</em> in your subject line so it can be flagged.
          </p>
        </div>

        {/* Email card */}
        <div
          className="rounded-3xl p-8 md:p-10 bg-white border-2 shadow-xl"
          style={{ borderColor: "#d4af6a" }}
          data-testid="contact-email-card"
        >
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: "#068fa215", color: "#068fa2" }}
            >
              <Mail className="w-5 h-5" />
            </div>
            <p className="text-[10px] tracking-[0.3em] uppercase font-sans font-semibold text-[#25a1c2]">
              Business Email
            </p>
          </div>

          <a
            href={`mailto:${BUSINESS_EMAIL}`}
            className="block font-serif text-xl md:text-2xl text-[#042F2E] hover:text-[#068fa2] transition-colors break-all"
            data-testid="contact-email-link"
          >
            {BUSINESS_EMAIL}
          </a>

          <p className="text-sm text-[#475569] font-sans mt-4">
            Tap the address above to open your email app.
          </p>
        </div>

        {/* Other ways */}
        <div className="mt-14">
          <p className="text-[10px] tracking-[0.3em] uppercase font-sans font-semibold text-[#25a1c2] mb-5">
            Or Find Me Here
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
              href="https://instagram.com/mindbodyspirithygienetools"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-5 rounded-2xl bg-white border border-[#068fa2]/20 hover:border-[#068fa2] transition-all hover:-translate-y-0.5"
              data-testid="contact-instagram"
            >
              <p className="font-serif text-lg text-[#042F2E]">Instagram</p>
              <p className="text-xs text-[#068fa2] font-sans">@mindbodyspirithygienetools</p>
            </a>
            <a
              href="https://www.youtube.com/@MindBodySpiritHygiene"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-5 rounded-2xl bg-white border border-[#068fa2]/20 hover:border-[#068fa2] transition-all hover:-translate-y-0.5"
              data-testid="contact-youtube"
            >
              <p className="font-serif text-lg text-[#042F2E]">YouTube</p>
              <p className="text-xs text-[#068fa2] font-sans">@MindBodySpiritHygiene</p>
            </a>
            <a
              href="https://pinterest.com/mindbodyspirithygiene"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-5 rounded-2xl bg-white border border-[#068fa2]/20 hover:border-[#068fa2] transition-all hover:-translate-y-0.5"
              data-testid="contact-pinterest"
            >
              <p className="font-serif text-lg text-[#042F2E]">Pinterest</p>
              <p className="text-xs text-[#068fa2] font-sans">@mindbodyspirithygiene</p>
            </a>
            <Link
              to="/links"
              className="block p-5 rounded-2xl bg-white border border-[#068fa2]/20 hover:border-[#068fa2] transition-all hover:-translate-y-0.5"
              data-testid="contact-links"
            >
              <p className="font-serif text-lg text-[#042F2E]">All Links</p>
              <p className="text-xs text-[#068fa2] font-sans">mindbodyspirithygienetools.com/links</p>
            </Link>
          </div>
        </div>

        {/* Privacy note */}
        <p className="text-sm text-[#475569] font-sans italic mt-12 text-center">
          For your privacy, no personal phone or address is shared online.<br />
          All correspondence goes through the email above.
        </p>

        {/* Sign-off */}
        <div className="mt-14 text-center">
          <p className="font-serif text-2xl italic text-[#042F2E]">
            Welcome home.{" "}
            <Heart className="inline w-5 h-5 fill-[#d4af6a]" style={{ color: "#d4af6a" }} />
          </p>
          <p className="text-sm text-[#068fa2] font-sans mt-2">
            — Temple & the Divine Team
          </p>
        </div>
      </main>
    </div>
  );
}
